import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import TomSelect from "tom-select"

// Progressive enhancement of the aircraft <select> on the weighing workspace
// shell (weighing_sessions/new). The native select stays the no-JS fallback;
// with JS it becomes a searchable, operator-grouped combobox whose options
// carry the airframe's identity plus fleet context (readiness badge, last
// weighed, open-weighing resume marker) serialized as JSON in data-data —
// the attribute Tom Select's dataAttr option reads.
export default class extends Controller {
  static targets = ["select", "hint", "submit"]
  static values = {
    createUrl: String,
    openLabel: { type: String, default: "Open weighing" },
    resumeLabel: { type: String, default: "Resume weighing session" }
  }

  connect() {
    // The schedule-linked and empty-fleet variants of the shell render no
    // select — the controller still sits on the form for the submit target.
    if (!this.hasSelectTarget) return

    this.ts = new TomSelect(this.selectTarget, {
      plugins: { dropdown_input: {} },
      // The full tenancy-scoped fleet must be listable — Tom Select's default
      // caps rendering at 50 options.
      maxOptions: null,
      create: false,
      allowEmptyOption: false,
      // Reads each <option>'s data-data JSON payload. Tom Select looks this
      // key up on el.dataset, where the data- prefix is already stripped —
      // so "data", not "data-data".
      dataAttr: "data",
      placeholder: this.selectTarget.dataset.placeholder || "Search registration, type, MSN or operator…",
      searchField: ["registration", "type", "msn", "operator"],
      // Relevance while searching, server order (operator A→Z, registration
      // A→Z within the group) when idle.
      sortField: [{ field: "$score" }, { field: "$order" }],
      lockOptgroupOrder: true,
      onChange: () => this.selectionChanged(),
      render: {
        option: (data, escape) => this.renderOption(data, escape),
        item: (data, escape) => this.renderItem(data, escape),
        optgroup_header: (data, escape) =>
          `<div class="optgroup-header ts-air-group"><i class="bi bi-building" aria-hidden="true"></i>${escape(data.label)}</div>`,
        no_results: (data, escape) => this.renderNoResults(data, escape)
      }
    })

    const dropdownInput = this.ts.dropdown.querySelector("input.dropdown-input")
    if (dropdownInput) dropdownInput.placeholder = "Type to filter the fleet…"

    // Links rendered inside the dropdown (the no-results "create" action)
    // never receive a click: Tom Select prevents default on dropdown
    // mousedown to keep control focus. Act on the mousedown instead — open
    // the in-page New-aircraft modal when the shell renders one, otherwise
    // navigate to the full-page form.
    this.onDropdownMousedown = (event) => {
      const link = event.target.closest("a[data-aircraft-select-visit]")
      if (!link) return
      event.preventDefault()
      event.stopPropagation()
      const modalElement = document.getElementById("new-aircraft-modal")
      if (modalElement) {
        this.ts.close()
        this.ts.blur()
        Modal.getOrCreateInstance(modalElement).show()
      } else {
        window.location.assign(link.href)
      }
    }
    this.ts.dropdown.addEventListener("mousedown", this.onDropdownMousedown)

    this.selectionChanged()
  }

  disconnect() {
    // Restores the native select so Turbo's page cache never snapshots (and
    // later re-enhances) the widget's generated markup.
    this.ts?.dropdown?.removeEventListener("mousedown", this.onDropdownMousedown)
    this.ts?.destroy()
    this.ts = null
  }


  renderOption(data, escape) {
    const badge = data.stateLabel
      ? `<span class="ts-air-badge ts-air-badge--${escape(data.stateTone || "muted")}">${escape(data.stateLabel)}</span>`
      : ""
    const weighed = data.lastWeighed
      ? `<span class="ts-air-weighed">Last weighed ${escape(data.lastWeighed)}</span>`
      : `<span class="ts-air-weighed">Never weighed</span>`
    const type = [data.type, data.msn].filter(Boolean).join(" · ")
    return `
      <div class="ts-air-option" role="presentation">
        <div class="ts-air-option__id">
          <span class="ts-air-option__reg">${escape(data.registration || data.text)}</span>
          <span class="ts-air-option__type">${escape(type)}</span>
        </div>
        <div class="ts-air-option__meta">
          ${badge}
          ${weighed}
        </div>
      </div>`
  }

  renderItem(data, escape) {
    // The operator rides on the selected chip: the session's operator is
    // derived from the aircraft, so this is where the user reads it.
    const type = [data.type, data.msn, data.operator].filter(Boolean).join(" · ")
    const resume = data.resume
      ? '<span class="ts-air-badge ts-air-badge--info ms-auto">Open weighing</span>'
      : ""
    return `
      <div class="ts-air-item">
        <i class="bi bi-airplane-fill" aria-hidden="true"></i>
        <span class="ts-air-item__reg">${escape(data.registration || data.text)}</span>
        <span class="ts-air-item__type">${escape(type)}</span>
        ${resume}
      </div>`
  }

  renderNoResults(data, escape) {
    const create = this.hasCreateUrlValue
      ? `<a href="${escape(this.createUrlValue)}" data-aircraft-select-visit class="ts-air-create">
           <i class="bi bi-plus-lg" aria-hidden="true"></i>Create a new aircraft…
         </a>`
      : ""
    return `<div class="no-results">No aircraft matches “${escape(data.input)}”.${create}</div>`
  }

  selectionChanged() {
    const data = this.ts?.options?.[this.ts.getValue()]
    this.updateHint(data)
    this.updateSubmit(data)
  }

  updateHint(data) {
    if (!this.hasHintTarget) return
    if (!data) {
      this.hintTarget.innerHTML = this.hintHTML(
        "bi-info-circle",
        "Search by registration, type, MSN or operator."
      )
      return
    }
    const reg = this.escape(data.registration || data.text)
    const weighed = data.lastWeighed ? ` Last weighed ${this.escape(data.lastWeighed)}.` : " Never weighed before."
    if (data.resume) {
      // No last-weighed sentence here: the open draft's own timestamp is what
      // the fleet-wide map reports, and "last weighed today" would mislead.
      this.hintTarget.innerHTML = this.hintHTML(
        "bi-arrow-repeat",
        `<strong>${reg}</strong> has an open weighing — opening the workspace resumes it instead of starting another.`,
        "resume"
      )
    } else {
      this.hintTarget.innerHTML = this.hintHTML(
        "bi-plus-circle",
        `Opens a new draft workspace for <strong>${reg}</strong>.${weighed}`
      )
    }
  }

  updateSubmit(data) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.textContent = data?.resume ? this.resumeLabelValue : this.openLabelValue
    // form-submit restores this label after a failed submit; keep it current.
    this.submitTarget.dataset.originalText = this.submitTarget.textContent
  }

  hintHTML(icon, message, tone) {
    const toneClass = tone ? ` aircraft-picker__hint-inner--${tone}` : ""
    return `<span class="aircraft-picker__hint-inner${toneClass}"><i class="bi ${icon}" aria-hidden="true"></i><span>${message}</span></span>`
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
